import { useEffect } from 'react';
import { useField } from 'formik';
import { MenuItem } from '@mui/material';
import { captureException } from '@sentry/react';

import { controlError } from 'utils';

import { Icon } from '../icons';
import { Input, InputProps } from '../input';

export interface SelectOption {
  label: string;
  value: string | number;
}

export interface SelectFieldProps extends Omit<
  InputProps,
  'name' | 'children'
> {
  name: string;
  label?: string;
  multiple?: boolean;
  placeholder?: string;
  options: SelectOption[];
}

export function SelectField({
  name,
  label,
  options,
  onChange,
  multiple,
  ...props
}: SelectFieldProps) {
  const [field, meta] = useField<string | string[]>(name);
  const errorText = controlError(meta, name, label);

  useEffect(() => {
    if (multiple && !Array.isArray(field.value)) {
      captureException(
        new Error(
          `SelectField "${name}": expected array but got ${typeof field.value}`
        ),
        {
          contexts: {
            selectField: { name, value: field.value },
          },
          tags: {
            fieldName: name,
            component: 'SelectField',
          },
        }
      );
    }
  }, [field.value, multiple, name]);

  return (
    <Input
      {...props}
      {...field}
      select
      fullWidth
      label={label}
      error={!!errorText}
      helperText={errorText}
      slotProps={{
        select: { multiple },
      }}
      onChange={(event) => {
        field.onChange(event);
        onChange?.(event);
      }}
    >
      {options.map((option) => {
        const isSelected = multiple
          ? field.value.includes(option.value as string)
          : field.value === option.value;

        return (
          <MenuItem
            key={option.value}
            value={option.value}
            className="relative pr-11"
          >
            {option.label}
            {multiple && isSelected && (
              <Icon.CheckboxOn className="absolute top-1/2 right-4 -translate-y-1/2" />
            )}
          </MenuItem>
        );
      })}
    </Input>
  );
}
